import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { StatusChip, type StatusTone } from '../abf/components/StatusChip';
import { Note } from '../abf/onboarding/review/contractReviewStyles';
import { INK } from '../abf/onboarding/review/reviewStyles';
import { GridTable, Panel, Table, Toolbar, money, words } from '../abf/board/boardShared';
import { loan360Path } from '../../utils/loanNavigation';
import { getPcBoard, pcErrorMessage } from './api';
import type { PcBoard, PcBoardRow } from './types';

/**
 * The private credit register (walkthrough finding F-PCP-014): the facilities the fund holds, as the official
 * book states them — facility, fund, type, commitment, funded, unfunded, official exposure, market value, rate,
 * status, maturity — with a search box and a row that opens the facility. It is the board's own row model with
 * the attention columns off; the legacy positions dashboard is no longer the register.
 */

const Dense = styled(Table)`
  th, td { white-space: nowrap; }
  td.wrap { white-space: normal; min-width: 220px; }
  .sub { display: block; font-size: 11.5px; color: ${INK.muted}; margin-top: 2px; }
  .muted { color: ${INK.faint}; }
`;

const facilityWords = (type: string | null) => !type ? '—' : ({
  FIRST_LIEN: 'First lien TL', SECOND_LIEN: 'Second lien TL', UNITRANCHE: 'Unitranche', DELAYED_DRAW: 'Delayed draw',
  REVOLVER: 'Revolver', MEZZANINE: 'Mezzanine', HOLDCO_PIK: 'HoldCo PIK', TERM_LOAN: 'Term loan',
} as Record<string, string>)[type] || words(type);
const statusTone = (row: PcBoardRow): StatusTone => row.nonAccrual ? 'critical' : row.status === 'ACTIVE' ? 'done' : row.status === 'MATURED' || row.status === 'REPAID' ? 'neutral' : 'progress';
const pct = (num: number | null | undefined, den: number | null | undefined) =>
  num === null || num === undefined || !den ? null : Math.round((Number(num) / Number(den)) * 100);

const PrivateCreditRegister: React.FC = () => {
  const [board, setBoard] = useState<PcBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  useEffect(() => { let alive = true; getPcBoard(false, false).then(b => { if (alive) setBoard(b); }).catch(e => { if (alive) setError(pcErrorMessage(e, 'The register could not be read.')); }); return () => { alive = false; }; }, []);

  const rows = useMemo(() => {
    const all = board?.facilities ?? [];
    const q = search.trim().toLowerCase();
    const matched = q ? all.filter(r => [r.borrowerName, r.loanName, r.loanCode, r.portfolioCode, r.portfolioName, r.sponsorName, r.facilityType]
      .some(v => v && String(v).toLowerCase().includes(q))) : all;
    return [...matched].sort((a, b) => a.borrowerName.localeCompare(b.borrowerName));
  }, [board, search]);
  const held = rows.filter(r => r.position);
  const defined = rows.filter(r => !r.position);
  const currency = rows[0]?.currency || 'USD';
  const sum = (pick: (r: PcBoardRow) => number | null | undefined) => held.reduce((acc, r) => acc + Number(pick(r) || 0), 0);

  return <Panel>
    <h3 style={{ margin: '0 0 4px' }}>Facility register</h3>
    <p style={{ margin: '0 0 10px', color: INK.muted, fontSize: 13 }}>
      {board ? `${held.length} held in a fund${defined.length ? `, ${defined.length} defined and not yet held` : ''} · business date ${board.businessDate}. Commitment, funded and unfunded as recorded on the facility; exposure and market value from the official positions.` : 'Reading the register…'}
    </p>
    <Toolbar>
      <label>Search <input value={search} onChange={e => setSearch(e.target.value)} placeholder="borrower, facility, fund, sponsor" /></label>
      <span style={{ color: INK.faint, fontSize: 12 }}>{held.length ? `Totals: ${money(sum(r => r.currentCommitment), currency)} committed · ${money(sum(r => r.fundedAmount), currency)} funded · ${money(sum(r => r.position?.exposure), currency)} exposure · ${money(sum(r => r.position?.marketValue), currency)} market value` : ''}</span>
    </Toolbar>
    {error && <Note role="alert">{error}</Note>}
    <GridTable><Dense>
      <thead><tr>
        <th className="sticky">Facility</th><th>Fund</th><th>Type</th><th>Status</th>
        <th className="num">Commitment</th><th className="num">Funded</th><th className="num">Unfunded</th>
        <th className="num">Official exposure</th><th className="num">Market value</th>
        <th>All-in rate</th><th>Maturity</th><th>Covenants</th>
      </tr></thead>
      <tbody>
        {held.map(r => <tr key={r.loanId}>
          <td className="wrap sticky"><Link to={loan360Path('PRIVATE_CREDIT', r.loanId)}>{r.borrowerName}</Link><span className="sub">{r.loanName || r.loanCode}{r.sponsorName ? ` · ${r.sponsorName}` : ''}</span></td>
          <td>{r.portfolioCode || <span className="muted">—</span>}<span className="sub">{r.portfolioName || ''}</span></td>
          <td>{facilityWords(r.facilityType)}{r.seniority ? <span className="sub">{words(r.seniority)}</span> : null}</td>
          <td><StatusChip tone={statusTone(r)} label={r.nonAccrual ? 'non-accrual' : words(r.status || '')} /></td>
          <td className="num">{money(r.currentCommitment, r.currency)}</td>
          <td className="num">{money(r.fundedAmount, r.currency)}{pct(r.fundedAmount, r.currentCommitment) != null ? <span className="sub">{pct(r.fundedAmount, r.currentCommitment)}% drawn</span> : null}</td>
          <td className="num">{money(r.unfundedAmount, r.currency)}{r.ddtlExpiry ? <span className="sub">DDTL to {r.ddtlExpiry}</span> : null}</td>
          <td className="num">{money(r.position?.exposure, r.currency)}<span className="sub">as of {r.position?.asOfDate || board?.businessDate}</span></td>
          <td className="num">{money(r.position?.marketValue, r.currency)}</td>
          <td>{r.allInRate != null ? `${Number(r.allInRate).toFixed(2)}%` : '—'}<span className="sub">{[r.baseRate, r.spreadBps != null ? `+ ${Number(r.spreadBps)} bps` : null].filter(Boolean).join(' ')}</span></td>
          <td>{r.maturityDate || '—'}</td>
          <td>{r.covenants.total ? `${r.covenants.total} defined` : '—'}{r.covenants.total ? <span className="sub">{[r.covenants.breached ? `${r.covenants.breached} breached` : null, r.covenants.warning ? `${r.covenants.warning} warning` : null, r.covenants.untested ? `${r.covenants.untested} untested` : null].filter(Boolean).join(' · ') || 'all passed'}</span> : null}</td>
        </tr>)}
        {board && held.length === 0 && <tr><td colSpan={12} className="muted">{search ? 'No facility matches the search.' : 'No facility is held in a fund yet.'}</td></tr>}
      </tbody>
    </Dense></GridTable>
    {defined.length > 0 && <>
      <h4 style={{ margin: '16px 0 4px' }}>Defined, not yet held</h4>
      <p style={{ margin: '0 0 8px', color: INK.muted, fontSize: 13 }}>Reference data only: the facility and its terms exist, no acquisition has posted. Not exposure.</p>
      <GridTable><Dense>
        <thead><tr><th>Facility</th><th>Fund</th><th>Type</th><th className="num">Commitment</th><th>Maturity</th></tr></thead>
        <tbody>{defined.map(r => <tr key={r.loanId}>
          <td className="wrap"><Link to={loan360Path('PRIVATE_CREDIT', r.loanId)}>{r.borrowerName}</Link><span className="sub">{r.loanName || r.loanCode}</span></td>
          <td>{r.portfolioCode || <span className="muted">no portfolio</span>}</td>
          <td>{facilityWords(r.facilityType)}</td>
          <td className="num">{money(r.currentCommitment, r.currency)}</td>
          <td>{r.maturityDate || '—'}</td>
        </tr>)}</tbody>
      </Dense></GridTable>
    </>}
  </Panel>;
};

export default PrivateCreditRegister;
